import { useEffect, useState, type CSSProperties } from 'react';
import type { YutResult } from '../shared/types';
import { RESULT_NAMES } from '../shared/types';
import './YutThrow.css';

/** Final face always comes from the authoritative result; the seed only changes the toss pose. */
export function stickPose(id: string, index: number, flatUp: boolean) {
  let seed = index + 1;
  for (const char of id) seed = (Math.imul(seed, 31) + char.charCodeAt(0)) >>> 0;
  return {
    left: [23, 42, 61, 78][index], top: [53, 40, 57, 44][index],
    angle: [-28, 19, -12, 31][index] + (seed % 15) - 7,
    face: flatUp ? 0 : 180, delay: index * 32,
    drift: (seed % 61) - 30,
  };
}

export function YutThrowStage({ result, animate = false, charging = false, compact = false }: { result?: YutResult; animate?: boolean; charging?: boolean; compact?: boolean }) {
  const [landed, setLanded] = useState(!animate);
  useEffect(() => {
    setLanded(!animate);
    if (!animate) return;
    const timer = setTimeout(() => setLanded(true), 1560);
    return () => clearTimeout(timer);
  }, [animate, result?.id]);
  const faces = result?.sticks ?? [true, false, true, false];
  return <div className={`yut-stage ${animate ? 'is-tossing' : ''} ${charging ? 'is-charging' : ''} ${compact ? 'is-compact' : ''}`} role="img" aria-label={animate && !landed ? '윷 네 개가 공중에서 돌아 바닥으로 떨어집니다.' : result ? `윷 네 개: ${faces.map((flat, i) => `${i + 1}번 ${flat ? '평평한 면' : '둥근 면'}`).join(', ')}. 결과 ${RESULT_NAMES[result.kind]}` : '던질 윷 네 개'} data-result={result?.kind} data-toss-phase={landed ? 'landed' : 'flying'}>
    <div className="throw-mat"><span className="mat-corner top-left" /><span className="mat-corner bottom-right" />
      {faces.map((flat, index) => {
        const pose = stickPose(result?.id ?? 'waiting', index, flat);
        const style = { left: `${pose.left}%`, top: `${pose.top}%`, '--angle': `${pose.angle}deg`, '--face': `${pose.face}deg`, '--spin': `${1080 + pose.face}deg`, '--delay': `${pose.delay}ms`, '--drift': `${pose.drift}px`, '--turn': `${pose.angle + 160}deg` } as CSSProperties;
        return <div className="stick-slot" style={style} key={index}>
          <i className="stick-floor-shadow" /><i className="landing-ring" />
          <div className="stick-flight"><div className="wooden-stick" data-flat-up={flat}>
            <div className="stick-face flat"><span className="wood-line" />{index === 0 ? <b className="backdo-mark">×</b> : <span className="stick-notches"><i /><i /><i /></span>}<small>{index + 1}</small></div>
            <div className="stick-face round"><span className="wood-line" /><span className="wood-ridge" /></div>
            <div className="stick-edge left" /><div className="stick-edge right" />
          </div></div>
        </div>;
      })}
    </div>
    {result && !charging && <div className={`throw-result ${result.steps >= 4 ? 'bonus-result' : ''}`}><b>{RESULT_NAMES[result.kind]}</b><span>{result.steps >= 4 ? `${result.steps}칸 · 한 번 더!` : result.steps === -1 ? '뒤로 한 칸' : `${result.steps}칸 이동`}</span></div>}
    {charging && <div className="charge-caption">놓을 준비…</div>}
    {animate && <span className="throw-call" aria-hidden="true">던져!</span>}
  </div>;
}
